import type { InputParser } from "../types";

// Stored numbers come as 10 digits or with the US country code (E.164
// "+14155551234", "14155551234"); the mask holds the 10-digit national number.
const toNationalDigits = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  return digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits;
};

const formatPhone = (value: string): string => {
  const digits = toNationalDigits(value);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

function tenDigitTelParser(): InputParser<number | null>;
function tenDigitTelParser(opts: { asString: true }): InputParser<string>;
function tenDigitTelParser(opts?: {
  asString?: boolean;
}): InputParser<number | null> | InputParser<string> {
  const asString = opts?.asString ?? false;

  if (asString) {
    return {
      inputMode: "tel",
      validate: (raw) =>
        /^[\d() -]*$/.test(raw) && raw.replace(/\D/g, "").length <= 10,
      parse: (raw) => raw.replace(/\D/g, ""),
      format: (value: string) => {
        if (value === "") return "";
        return formatPhone(value);
      },
      toEditable: (value: string) => (value === "" ? "" : formatPhone(value)),
      formatWhileEditing: true
    } as InputParser<string>;
  }

  return {
    inputMode: "tel",
    validate: (raw) =>
      /^[\d() -]*$/.test(raw) && raw.replace(/\D/g, "").length <= 10,
    parse: (raw) => {
      const digits = raw.replace(/\D/g, "");
      if (digits === "") return null;
      return parseInt(digits, 10);
    },
    format: (value: number | null) => {
      if (value === null) return "";
      return formatPhone(String(value));
    },
    toEditable: (value: number | null) => {
      if (value === null) return "";
      return formatPhone(String(value));
    },
    formatWhileEditing: true
  };
}

export { tenDigitTelParser };
